import { IPromise } from 'angular';
import ActionDispatcherService from '../../common/actions/core/action-dispatcher.service';
import MixpanelService from '../../common/services/analytics/mixpanel.service';
import NotificationService from '../../common/services/notification/notification.service';
import {
  DiscountedPriceItem,
  PackageThumbnail,
  PromocodeCalculateResponse,
  PromocodeCalculateSummary,
  PromocodeResponse,
  UserListeningPackagesResponse
} from '../../common/services/package/package.model';
import PackageService from '../../common/services/package/package.service';
import GlobalSpinnerService from '../../common/services/spinner/global-spinner.service';
import NoAuthService from '../../no-auth/services/noauth.service';
import { PromocodeHasPackageAction } from '../promocode-has-package/promocode-has-package.action';
import { PromocodeAction } from '../promocode-redirect/promocode-redirect.action';

/* @ngInject */
export default class PromocodeController {
  private promocode: string;
  private promocodeInput: string;
  private discount: PromocodeResponse;
  private packages: UserListeningPackagesResponse;
  private msisdn: string;
  private packet: PackageThumbnail;
  private validPromocode = false;

  constructor(
    private $translate,
    private globalSpinner: GlobalSpinnerService,
    private notificationService: NotificationService,
    private actionDispatcher: ActionDispatcherService,
    private packageService: PackageService,
    private noAuthService: NoAuthService,
    private mixpanelService: MixpanelService
  ) {}

  public $onInit() {
    if (this.promocode) {
      this.promocodeInput = this.promocode;
      this.checkPromocode();
    }
  }

  public checkPromocode() {
    if (!this.promocodeInput || this.promocodeInput.length < 1) {
      this.showWarning('promocode.error.valid');
      return;
    }

    // Her yeni istekte onceki kodun ozeti temizlenir, kart eski degerleri gostermeye devam etmesin.
    this.clearCalculateSummary();
    this.globalSpinner.startIfNotActivated();

    const promocode = this.promocodeInput;
    const sardisOfferId = this.packet.sardisOfferId;

    // OTP ile dogrulanmis msisdn varsa istekler noauth/subscribe altina gider.
    if (this.msisdn) {
      this.applyCalculate(
        this.noAuthService.calculatePromocode(this.msisdn, promocode, sardisOfferId),
        () => this.noAuthService.checkPromocode(this.msisdn, promocode, sardisOfferId),
        promocode
      );
      return;
    }

    this.applyCalculate(
      this.packageService.calculatePromocode(promocode, sardisOfferId),
      () => this.packageService.checkPromocode(promocode, sardisOfferId),
      promocode
    );
  }

  /**
   * v2/checkPromocode cevabini kart ozetine cevirir.
   * Istek basarisiz olur ya da discountedPrices bos gelirse eski GET
   * checkPromocode'a dusulur; o da basarisiz olursa kullaniciya uyari gosterilir.
   */
  private applyCalculate(
    calculate: IPromise<PromocodeCalculateResponse>,
    legacyCheck: () => IPromise<PromocodeResponse>,
    promocode: string
  ) {
    calculate
      .then(result => {
        const normalized = this.normalizeCalculateResult(result);
        this.checkPromocodeResult(normalized, promocode, false);
      })
      .catch(() => {
        legacyCheck()
          .then(result => this.checkPromocodeResult(result, promocode))
          .catch(() => this.onPromocodeRequestFailed('promocode.error.calculateFallback'));
      });
  }

  public cancelPromocode() {
    this.promocode = undefined;
    this.discount = undefined;
    this.validPromocode = false;
    angular.element('#promocodeError').text('');
  }

  private clearCalculateSummary() {
    if (this.discount) {
      this.discount.calculateSummary = undefined;
    }
  }

  private checkPromocodeResult(
    result,
    promocode: string,
    isLegacyResult: boolean = true
  ) {
    if (result) {
      if (result.valid) {
        this.promocode = promocode;
        this.validPromocode = true;
        angular.element('#promocodeError').text('');
        this.mixpanelService.track('Promocode Added');
        this.discount = result;

        if (isLegacyResult && this.discount.discountedPrice) {
          this.discount.discountedPrice = this.discount.discountedPrice.replace('.', ',');
        }
        if (isLegacyResult && this.discount.discountedRenewalCount) {
          this.discount.discountedRenewalCount = (parseInt(this.discount.discountedRenewalCount, 10) + 1) + '';
        }
      } else {
        this.handleInvalidPromocode(result);
      }
    } else {
      this.mixpanelService.track('Promocode Failed');
      this.showWarning('promocode.error.default');
    }

    this.globalSpinner.stop();
  }

  private handleInvalidPromocode(result: PromocodeResponse) {
    let redirected = false;
    let hasPackage = false;

    if (result.redirectOfferId != '' && this.packages.userListeningPackages) {
      const userListeningPackages = this.packages.userListeningPackages;
      for (let index = 0; index < userListeningPackages.length; index++) {
        if (userListeningPackages[index].listeningPackage.offerId === result.redirectOfferId) {
          hasPackage = true;
          const url = window.location.href.split('/buy/')[0] + (this.msisdn ? '/offers/' : '/');
          this.globalSpinner.stop();
          this.actionDispatcher.dispatch(new PromocodeHasPackageAction(url));
          break;
        }
      }
    }

    if (result.redirectOfferId != '' && this.packages.offerablePackages && hasPackage == false) {
      const offerables = this.packages.offerablePackages;
      for (let index = 0; index < offerables.length; index++) {
        if (offerables[index].offerId === result.redirectOfferId) {
          redirected = true;
          this.globalSpinner.stop();
          this.actionDispatcher.dispatch(new PromocodeAction(result.redirectOfferId, result.redirectPackageName, this.promocode));
          break;
        }
      }
    }

    if (redirected == false) {
      this.mixpanelService.track('Promocode Failed');
      const promocodeError = angular.element('#promocodeError');
      if (result.errorMessage !== '') {
        promocodeError.text(result.errorMessage);
      } else {
        this.$translate('promocode.error.default').then(value => {
          promocodeError.text(value);
        });
      }
    }
  }

  private normalizeCalculateResult(result: PromocodeCalculateResponse): PromocodeResponse {
    const discountedPrices = result && result.discountedPrices ? result.discountedPrices : [];
    if (!discountedPrices.length) {
      throw new Error('CALCULATE_EMPTY_RESULT');
    }

    const trialCount = this.getInitialTrialCount(discountedPrices);
    const hasTrial = trialCount > 0;
    const trialPriceItem = hasTrial ? discountedPrices[trialCount - 1] : null;
    const postTrialItem = this.getFirstPostTrialItem(discountedPrices, trialCount);
    const promocodeAppliedCount = discountedPrices.filter(item => item && item.promocodeApplied === true).length;
    const computedDiscountRate = this.getComputedDiscountRate(discountedPrices, result);

    return {
      valid: true,
      discountRate: angular.isNumber(computedDiscountRate) ? computedDiscountRate.toString() : '',
      discountedPrice: this.toLegacyPrice(postTrialItem ? postTrialItem.discountedPrice : (trialPriceItem ? trialPriceItem.discountedPrice : null)),
      discountedRenewalCount: promocodeAppliedCount > 0 ? promocodeAppliedCount.toString() : '',
      errorCode: '',
      errorMessage: '',
      redirectOfferId: '',
      redirectPackageName: '',
      discountedPrices,
      trialCount,
      trialDiscountedPrice: trialPriceItem ? trialPriceItem.discountedPrice : undefined,
      postTrialOriginalPrice: postTrialItem ? postTrialItem.price : undefined,
      postTrialDiscountedPrice: postTrialItem ? postTrialItem.discountedPrice : undefined,
      promocodeAppliedCount,
      computedDiscountRate,
      hasTrial,
      hasPostTrial: !!postTrialItem,
      calculateSummary: this.buildCalculateSummary(discountedPrices)
    };
  }

  /**
   * discountedPrices dizisini kart icin siniflandirir.
   *
   * Ilk elemanin trial degeri hangi tasarimin calisacagini belirler.
   *
   * Yesil kutudaki ay sayisi ise dogrudan indirim uygulanan eleman sayisidir.
   * Ilk elemanin promocodeApplied degeri kampanya penceresinin bedava ayi kapsayip
   * kapsamadigini gosterir, ama bu zaten dizinin uzunluguna yansidigi icin sayimi
   * ayrica etkilemez:
   *   1 bedava + 12 indirimli ay (promocodeApplied[0] = false) -> 12
   *   1 bedava + 11 indirimli ay (promocodeApplied[0] = true)  -> 11
   * Dizide indirim bitiminden sonraki tam fiyatli aylar da gelirse sayima girmez.
   *
   * Promokod trial aylarin tamamini kapsamadiginda fiyat alanlari bedava ay
   * sayimi icin guvenilir degildir: kapsam disinda kalan trial ayin
   * discountedPrice degeri liste fiyati gelebiliyor. Bu durumda sayim trial
   * bayragina sabitlenir ve freeCount trialCount ile ezilir.
   *
   * Ezme yalnizca trial bayragi fiyat tabanli sayimdan FAZLA bedava ay
   * gosterdiginde devreye girer. Bedava aydaki promocodeApplied === false tek
   * basina ezme sebebi degildir; yukarida anlatildigi gibi bu yaygin durum
   * kampanya penceresinin bedava ayi kapsamadigini gosterir, trial ayin kod
   * disinda kaldigini degil.
   */
  private buildCalculateSummary(discountedPrices: DiscountedPriceItem[]): PromocodeCalculateSummary {
    const first = discountedPrices[0];
    const totalCount = discountedPrices.length;
    const trialCount = discountedPrices.filter(item => item && item.trial === true).length;
    const promocodeAppliedTrialCount = discountedPrices.filter(
      item => item && item.trial === true && item.promocodeApplied === true
    ).length;
    const leadingFreeCount = this.getLeadingFreeCount(discountedPrices);
    const trialOverridesFreeCount =
      trialCount > leadingFreeCount && promocodeAppliedTrialCount < trialCount;
    const freeCount = trialOverridesFreeCount ? trialCount : leadingFreeCount;
    const paidItem = discountedPrices[freeCount];

    return {
      totalCount,
      trialCount,
      promocodeAppliedTrialCount,
      trialOverridesFreeCount,
      freeCount,
      startsWithTrial: !!(first && first.trial === true),
      promocodeAppliedOnFirst: !!(first && first.promocodeApplied === true),
      allFree: freeCount === totalCount,
      standardPrice: paidItem ? paidItem.price : undefined,
      discountAmount: this.getDiscountAmount(paidItem),
      discountedPrice: paidItem ? paidItem.discountedPrice : undefined,
      discountMonthCount: this.getDiscountedMonthCount(discountedPrices)
    };
  }

  /**
   * "Kod ile indirim" satirinda yazilan tutar.
   * calculate cevabi discountAmount alanini her zaman gondermiyor; gelmediginde
   * liste fiyati ile indirimli fiyatin farki kullanilir.
   */
  private getDiscountAmount(item: DiscountedPriceItem): number {
    if (!item) {
      return undefined;
    }

    if (angular.isNumber(item.discountAmount)) {
      return item.discountAmount;
    }

    if (angular.isNumber(item.price) && angular.isNumber(item.discountedPrice)) {
      return item.price - item.discountedPrice;
    }

    return undefined;
  }

  /** Ucretli olup indirim uygulanmis eleman sayisi. Bedava aylar bu sayima girmez. */
  private getDiscountedMonthCount(discountedPrices: DiscountedPriceItem[]): number {
    return discountedPrices.filter(item => this.isDiscountedPaidItem(item)).length;
  }

  /**
   * Bedava ay, musterinin o ay icin odedigi tutarin sifir oldugu aydir.
   * calculate cevabinda bedava ayin price alani liste fiyatini tasiyabilir, sifir
   * olan discountedPrice'tir; bu yuzden sayim odenen tutar uzerinden yapilir.
   */
  private isFreeItem(item: DiscountedPriceItem): boolean {
    if (!item) {
      return false;
    }

    const payablePrice = angular.isNumber(item.discountedPrice)
      ? item.discountedPrice
      : item.price;

    return angular.isNumber(payablePrice) && payablePrice === 0;
  }

  /** Hem odenen tutari sifirdan buyuk hem de liste fiyatinin altinda olan ay. */
  private isDiscountedPaidItem(item: DiscountedPriceItem): boolean {
    return !!(
      item &&
      angular.isNumber(item.price) &&
      angular.isNumber(item.discountedPrice) &&
      item.price > 0 &&
      item.discountedPrice > 0 &&
      item.discountedPrice < item.price
    );
  }

  private getLeadingFreeCount(discountedPrices: DiscountedPriceItem[]): number {
    let count = 0;
    for (let index = 0; index < discountedPrices.length; index++) {
      if (!this.isFreeItem(discountedPrices[index])) {
        break;
      }
      count++;
    }
    return count;
  }

  private getInitialTrialCount(discountedPrices: DiscountedPriceItem[]): number {
    let count = 0;
    for (let index = 0; index < discountedPrices.length; index++) {
      if (discountedPrices[index] && discountedPrices[index].trial === true) {
        count++;
      } else {
        break;
      }
    }
    return count;
  }

  private getFirstPostTrialItem(discountedPrices: DiscountedPriceItem[], trialCount: number): DiscountedPriceItem {
    for (let index = trialCount; index < discountedPrices.length; index++) {
      if (discountedPrices[index] && discountedPrices[index].trial === false) {
        return discountedPrices[index];
      }
    }
    return null;
  }

  private getComputedDiscountRate(discountedPrices: DiscountedPriceItem[], result: PromocodeCalculateResponse): number {
    const rateCandidate = discountedPrices.find(item => this.isDiscountedPaidItem(item));

    if (rateCandidate) {
      return Math.round(((rateCandidate.price - rateCandidate.discountedPrice) / rateCandidate.price) * 100);
    }

    if (result && result.campaignDetail && result.campaignDetail.discountType === 0 && angular.isNumber(result.campaignDetail.discount)) {
      return Math.round(result.campaignDetail.discount);
    }

    return undefined;
  }

  private toLegacyPrice(value: number): string {
    if (!angular.isNumber(value)) {
      return '';
    }
    return value.toFixed(2).replace('.', ',');
  }

  private onPromocodeRequestFailed(translateKey: string) {
    this.mixpanelService.track('Promocode Failed');
    this.showWarning(translateKey);
    this.globalSpinner.stop();
  }

  private showWarning(translateKey: string) {
    this.$translate(translateKey).then(value => {
      this.notificationService.showNotification({
        text: value,
        type: 'warning'
      });
    });
  }
}





// WEBPACK FOOTER //
// ./src/app/components/promocode/promocode.controller.ts